import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row, Col, Card, CardBody, CardHeader, Modal, ModalHeader, ModalBody, ModalFooter, Alert } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import SearchOption from '../../Components/Common/SearchOption';
import { authUserManager } from '../../helpers/api_helper';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { fetchLibrarySongs, addLibrarySong, removeLibrarySong } from '../../slices/songLibrarySong/thunk';
import { clearLibrarySongMessages } from '../../slices/songLibrarySong/reducer';
import { fetchSongs, updateSong } from '../../slices/song/list/thunk';
import { selectAllSongs } from '../../slices/song/list/reducer';
import { fetchSongLibrary } from '../../slices/songLibrary/thunk';
import { selectCurrentLibrary } from '../../slices/songLibrary/reducer';

const LibraryDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [globalFilter, setGlobalFilter] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [songToDelete, setSongToDelete] = useState(null);
  const [songToEdit, setSongToEdit] = useState(null);

  const { librarySongs, totalItems, loading, error, success } = useSelector((state) => state.SongLibrarySong);
  const globalSongs = useSelector(selectAllSongs);
  const currentLibrary = useSelector(selectCurrentLibrary);
  const user = authUserManager.getAuthUser();

  // Fetch global songs for SearchOption
  useEffect(() => {
    dispatch(fetchSongs());
  }, [dispatch]);

  useEffect(() => {
    if (id) {
      dispatch(fetchSongLibrary(id));
      dispatch(fetchLibrarySongs({
        libraryId: id,
        page: pageIndex + 1,
        itemsPerPage: pageSize,
        search: globalFilter
      }));
    }
  }, [dispatch, id, pageIndex, pageSize, globalFilter]);

  // Format songs for SearchOption
  const songOptions = useMemo(() => {
    return globalSongs.map(song => ({
      value: song.id,
      label: `${song.artist} - ${song.title}`
    }));
  }, [globalSongs]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        dispatch(clearLibrarySongMessages());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, dispatch]);

  const toggleAddModal = () => {
    setIsAddModalOpen(!isAddModalOpen);
    if (isAddModalOpen) {
      addForm.resetForm();
      dispatch(clearLibrarySongMessages());
    }
  };

  const toggleEditModal = () => {
    setIsEditModalOpen(!isEditModalOpen);
    if (isEditModalOpen) {
      setSongToEdit(null);
      editForm.resetForm();
      dispatch(clearLibrarySongMessages());
    }
  };

  const toggleDeleteModal = () => {
    setIsDeleteModalOpen(!isDeleteModalOpen);
    if (isDeleteModalOpen) {
      setSongToDelete(null);
      dispatch(clearLibrarySongMessages());
    }
  };

  const handleEdit = (song) => {
    setSongToEdit(song);
    editForm.setValues({
      artist: song.song?.artist || '',
      title: song.song?.title || ''
    });
    setIsEditModalOpen(true);
  };

  const handleDelete = (song) => {
    setSongToDelete(song);
    setIsDeleteModalOpen(true);
  };

  // Check if current user can edit a song
  const canEditSong = (song) => {
    if (!user) return false;
    // Admin can edit all songs
    if (user.role && user.role.includes('ROLE_ADMIN')) return true;
    // Club Admin can only edit songs they own
    // song.user might be an IRI string like "/api/users/{id}" or an object with id
    const songUserId = typeof song.song?.user === 'string'
      ? song.song.user.split('/').pop()
      : song.song?.user?.id;
    return songUserId === user.id;
  };

  const confirmDelete = () => {
    if (songToDelete) {
      dispatch(removeLibrarySong(songToDelete.id)).then(() => {
        dispatch(fetchLibrarySongs({
          libraryId: id,
          page: pageIndex + 1,
          itemsPerPage: pageSize,
          search: globalFilter
        }));
      });
      toggleDeleteModal();
    }
  };

  const addForm = useFormik({
    initialValues: {
      song: null,
    },
    validationSchema: Yup.object({
      song: Yup.mixed()
        .required(t('This field is required'))
        .test('is-valid-song', t('min.field.characters', { count: 5 }), (value) => {
          if (!value) return false;
          // If it's a number (selected from list - ID)
          if (typeof value === 'number') return true;
          // If it's a string (new song being created)
          if (typeof value === 'string' && value.length >= 5 && value.length <= 60) return true;
          return false;
        }),
    }),
    onSubmit: (values) => {
      let songValue = values.song;
      const payload = {
        libraryId: parseInt(id),
        song: {}
      };

      // If user selected existing song (number ID)
      if (typeof songValue === 'number') {
        payload.song.id = songValue;
        payload.song.artistTitle = null;
      }
      // If user typed new song (string)
      else if (typeof songValue === 'string') {
        payload.song.id = null;
        payload.song.artistTitle = songValue;
      }

      dispatch(addLibrarySong(payload)).then(() => {
        dispatch(fetchLibrarySongs({
          libraryId: id,
          page: pageIndex + 1,
          itemsPerPage: pageSize,
          search: globalFilter
        }));
      });
      toggleAddModal();
    },
  });

  const editForm = useFormik({
    initialValues: {
      artist: '',
      title: ''
    },
    validationSchema: Yup.object({
      artist: Yup.string()
        .required(t('This field is required'))
        .min(1, t('min.field.characters', { count: 1 }))
        .max(100, t('max.field.characters', { count: 100 })),
      title: Yup.string()
        .required(t('This field is required'))
        .min(1, t('min.field.characters', { count: 1 }))
        .max(100, t('max.field.characters', { count: 100 }))
    }),
    onSubmit: (values) => {
      if (songToEdit && songToEdit.song) {
        // Update the song entity
        dispatch(updateSong({
          id: songToEdit.song.id,
          data: {
            artist: values.artist,
            title: values.title
          }
        })).then(() => {
          // Refresh library songs to show updated data
          dispatch(fetchLibrarySongs({
            libraryId: id,
            page: pageIndex + 1,
            itemsPerPage: pageSize,
            search: globalFilter
          }));
        });
        toggleEditModal();
      }
    }
  });

  const columns = useMemo(
    () => [
      {
        header: t('Artist'),
        accessorFn: (row) => row.song?.artist || '',
        enableColumnFilter: false,
        cell: (cell) => cell.getValue(),
      },
      {
        header: t('Title'),
        accessorFn: (row) => row.song?.title || '',
        enableColumnFilter: false,
        cell: (cell) => cell.getValue(),
      },
      {
        header: t('Action'),
        cell: (cell) => (
          <div className="d-flex gap-2 justify-content-end">
            {canEditSong(cell.row.original) && (
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() => handleEdit(cell.row.original)}
              >
                <i className="ri-pencil-line"></i>
              </button>
            )}
            <button
              type="button"
              className="btn btn-sm btn-danger"
              onClick={() => handleDelete(cell.row.original)}
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          </div>
        ),
      },
    ],
    [t, user]
  );

  const pageCount = Math.ceil(totalItems / pageSize);

  const table = useReactTable({
    columns,
    data: librarySongs,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    manualFiltering: true,
    pageCount,
    state: {
      globalFilter,
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    onGlobalFilterChange: (value) => {
      setGlobalFilter(value);
      setPageIndex(0); // Reset to first page when searching
    },
    onPaginationChange: (updater) => {
      if (typeof updater === 'function') {
        const newState = updater({ pageIndex, pageSize });
        setPageIndex(newState.pageIndex);
        setPageSize(newState.pageSize);
      }
    },
  });

  return (
    <React.Fragment>
      <div className="page-content non-admin-layout">
        <Container fluid>
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0">{t('Song Libraries')}</h4>
            <div className="page-title-right">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item">
                  <Link to="/admin/song-libraries?tab=my-libraries">{t('Song Libraries')}</Link>
                </li>
                <li className="breadcrumb-item active">
                  {currentLibrary?.name || t('Library Songs')}
                </li>
              </ol>
            </div>
          </div>

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <div className="d-flex align-items-center gap-2">
                    <Link to="/admin/song-libraries?tab=my-libraries" className="btn btn-sm btn-light" title={t('My Libraries')}>
                      <i className="ri-arrow-left-line"></i>
                    </Link>
                    <h5 className="card-title mb-0">{currentLibrary?.name || t('Library Songs')}</h5>
                  </div>
                </CardHeader>

                <CardBody>
                  {error && (
                    <Alert color="danger">
                      {typeof error === 'string' ? t(error) : JSON.stringify(error)}
                    </Alert>
                  )}
                  {success && <Alert color="success">{t(success)}</Alert>}

                  <Row className="mb-3 align-items-center">
                    <Col sm={12} md={4}>
                      <button
                        type="button"
                        className="btn btn-label btn-success color-primary"
                        onClick={toggleAddModal}
                      >
                        <i className="ri-add-line label-icon align-middle fs-16 me-2"></i>
                        {t('Add Song')}
                      </button>
                    </Col>
                    <Col sm={12} md={4}>
                      <div className="search-box">
                        <input
                          type="text"
                          className="form-control search"
                          placeholder={t('Search songs...')}
                          value={globalFilter ?? ''}
                          onChange={(e) => setGlobalFilter(e.target.value)}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </Col>
                    <Col sm={12} md={4}>
                      <div className="d-flex align-items-center justify-content-md-end">
                        <span className="me-2">{t('Show')}</span>
                        <select
                          className="form-select"
                          style={{ width: 'auto' }}
                          value={pageSize}
                          onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setPageIndex(0);
                          }}
                        >
                          {[10, 25, 50].map((size) => (
                            <option key={size} value={size}>
                              {size}
                            </option>
                          ))}
                        </select>
                        <span className="ms-2">{t('entries')}</span>
                      </div>
                    </Col>
                  </Row>

                  {loading ? (
                    <div className="text-center">
                      <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">{t('Loading...')}</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <table className="table table-hover table-nowrap align-middle mb-0">
                          <thead className="table-light">
                            {table.getHeaderGroups().map((headerGroup) => (
                              <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                  <th key={header.id}>
                                    {header.isPlaceholder
                                      ? null
                                      : flexRender(header.column.columnDef.header, header.getContext())}
                                  </th>
                                ))}
                              </tr>
                            ))}
                          </thead>
                          <tbody>
                            {table.getRowModel().rows.length > 0 ? (
                              table.getRowModel().rows.map((row) => (
                                <tr key={row.id}>
                                  {row.getVisibleCells().map((cell) => (
                                    <td key={cell.id}>
                                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                  ))}
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={columns.length} className="text-center">
                                  {t('No songs found')}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      <Row className="align-items-center mt-2 g-3 text-center text-sm-start">
                        <div className="col-sm">
                          <div className="text-muted">
                            {t('Total songs')}: <span className="fw-semibold">{totalItems}</span>
                            {' | '}
                            {t('Showing')}: <span className="fw-semibold">{pageIndex * pageSize + 1}-{Math.min((pageIndex + 1) * pageSize, totalItems)}</span>
                          </div>
                        </div>
                        <div className="col-sm-auto">
                          <ul className="pagination pagination-separated pagination-md justify-content-center justify-content-sm-end mb-0">
                            <li className={!table.getCanPreviousPage() ? 'page-item disabled' : 'page-item'}>
                              <Link to="#" className="page-link" onClick={() => table.previousPage()}>
                                {t('Previous')}
                              </Link>
                            </li>
                            {pageIndex !== 0 && (
                              <li className="page-item">
                                <Link to="#" className="page-link" onClick={() => setPageIndex(0)}>
                                  1
                                </Link>
                              </li>
                            )}
                            {pageIndex > 1 && (
                              <li className="page-item disabled">
                                <span className="page-link">...</span>
                              </li>
                            )}
                            <li className="page-item active">
                              <Link to="#" className="page-link">
                                {pageIndex + 1}
                              </Link>
                            </li>
                            {pageIndex < pageCount - 2 && (
                              <li className="page-item disabled">
                                <span className="page-link">...</span>
                              </li>
                            )}
                            {pageIndex !== pageCount - 1 && pageCount > 1 && (
                                <li className="page-item">
                                  <Link
                                    to="#"
                                    className="page-link"
                                    onClick={() => setPageIndex(pageCount - 1)}
                                  >
                                    {pageCount}
                                  </Link>
                                </li>
                              )}
                            <li className={!table.getCanNextPage() ? 'page-item disabled' : 'page-item'}>
                              <Link to="#" className="page-link" onClick={() => table.nextPage()}>
                                {t('Next')}
                              </Link>
                            </li>
                          </ul>
                        </div>
                      </Row>
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Add Song Modal */}
      <Modal isOpen={isAddModalOpen} toggle={toggleAddModal}>
        <ModalHeader toggle={toggleAddModal}>{t('Add Song')}</ModalHeader>
        <form onSubmit={addForm.handleSubmit}>
          <ModalBody>
            {error && (
              <Alert color="danger">
                {typeof error === 'string' ? t(error) : JSON.stringify(error)}
              </Alert>
            )}
            <div className="mb-3">
              <SearchOption addForm={addForm} songOptions={songOptions} />
            </div>
          </ModalBody>
          <ModalFooter>
            <div className="hstack gap-2 justify-content-end">
              <button type="button" className="btn btn-label btn-light" onClick={toggleAddModal}>
                <i className="ri-close-circle-line label-icon align-middle fs-16 me-2"></i>
                {t('Close')}
              </button>
              <button type="submit" className="btn btn-label btn-success color-primary">
                <i className="ri-check-double-line label-icon align-middle fs-16 me-2"></i>
                {t('Add')}
              </button>
            </div>
          </ModalFooter>
        </form>
      </Modal>

      {/* Edit Song Modal */}
      <Modal isOpen={isEditModalOpen} toggle={toggleEditModal}>
        <ModalHeader toggle={toggleEditModal}>{t('Edit Song')}</ModalHeader>
        <form onSubmit={editForm.handleSubmit}>
          <ModalBody>
            {error && (
              <Alert color="danger">
                {typeof error === 'string' ? t(error) : JSON.stringify(error)}
              </Alert>
            )}
            <div className="mb-3">
              <label htmlFor="artist" className="form-label">{t('Artist')}</label>
              <input
                type="text"
                className={`form-control ${editForm.touched.artist && editForm.errors.artist ? 'is-invalid' : ''}`}
                id="artist"
                name="artist"
                value={editForm.values.artist}
                onChange={editForm.handleChange}
                onBlur={editForm.handleBlur}
              />
              {editForm.touched.artist && editForm.errors.artist && (
                <div className="invalid-feedback">{editForm.errors.artist}</div>
              )}
            </div>
            <div className="mb-3">
              <label htmlFor="title" className="form-label">{t('Title')}</label>
              <input
                type="text"
                className={`form-control ${editForm.touched.title && editForm.errors.title ? 'is-invalid' : ''}`}
                id="title"
                name="title"
                value={editForm.values.title}
                onChange={editForm.handleChange}
                onBlur={editForm.handleBlur}
              />
              {editForm.touched.title && editForm.errors.title && (
                <div className="invalid-feedback">{editForm.errors.title}</div>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <div className="hstack gap-2 justify-content-end">
              <button type="button" className="btn btn-label btn-light" onClick={toggleEditModal}>
                <i className="ri-close-circle-line label-icon align-middle fs-16 me-2"></i>
                {t('Close')}
              </button>
              <button type="submit" className="btn btn-label btn-success color-primary">
                <i className="ri-check-double-line label-icon align-middle fs-16 me-2"></i>
                {t('Save')}
              </button>
            </div>
          </ModalFooter>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} toggle={toggleDeleteModal} centered>
        <ModalBody>
          <div className="mt-2 text-center">
            <i className="ri-delete-bin-line display-5 text-danger"></i>
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{t('Are you sure?')}</h4>
              <p className="text-muted mx-4 mb-0">
                {t('Are you sure you want to delete this song from the library?')}
                <br />
                <strong>
                  {songToDelete?.song?.artist} - {songToDelete?.song?.title}
                </strong>
              </p>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="hstack gap-2 justify-content-center w-100">
            <button type="button" className="btn btn-label btn-light" onClick={toggleDeleteModal}>
              <i className="ri-close-circle-line label-icon align-middle fs-16 me-2"></i>
              {t('Close')}
            </button>
            <button type="button" className="btn btn-label btn-danger" onClick={confirmDelete}>
              <i className="ri-delete-bin-line label-icon align-middle fs-16 me-2"></i>
              {t('Delete')}
            </button>
          </div>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default LibraryDetail;
